import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star, Check, MapPin, Clock, ShieldCheck, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Accreditations from "@/components/site/Accreditations";
import CTASection from "@/components/site/CTASection";
import SEO from "@/components/site/SEO";
import { SERVICES, TESTIMONIALS, AREAS, COMPANY } from "@/lib/site";

const HERO_IMG = "https://images.unsplash.com/photo-1593786267440-550458cc882a?crop=entropy&cs=srgb&fm=jpg&w=1900&q=80";

export default function Home() {
  return (
    <>
      <SEO
        title="L F Building & Maintenance Ltd — Essex & Suffolk Builders"
        description="Family-run builders serving Essex, Suffolk & surrounding areas. Residential builds, extensions, roofing, loft conversions, kitchens & bathrooms. Free quotes."
        path="/"
      />

      {/* HERO */}
      <section className="relative bg-brand-ink text-white overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Modern residential building project" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/80 to-brand-ink/40" />
        </div>

        <div className="relative container-lf py-28 md:py-44 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-8">
            <div className="flex items-center gap-4">
              <span className="eyebrow text-brand">Essex · Suffolk · Since 2022</span>
            </div>
            <h1 className="font-display font-bold uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.88]">
              Building <span className="text-brand">better</span><br />
              homes for<br />the East.
            </h1>
            <p className="text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
              From full residential builds to loft conversions, extensions and premium kitchens — L F Building delivers
              craft-first construction across Essex, Suffolk and the surrounding counties.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/contact" data-testid="hero-cta-quote">
                <Button className="rounded-none bg-brand hover:bg-brand-hover text-white h-14 px-8 text-base font-semibold uppercase tracking-wider shadow-[6px_6px_0_0_#ffffff] hover:shadow-[10px_10px_0_0_#ffffff] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all">
                  Get a Free Quote <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/projects" data-testid="hero-cta-projects">
                <Button
                  variant="outline"
                  className="rounded-none bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-ink h-14 px-8 text-base font-semibold uppercase tracking-wider"
                >
                  View Projects
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex flex-col justify-end space-y-3 border-l border-white/20 pl-8">
            {[
              { k: "35+", v: "Total Years Experience" },
              { k: "50+", v: "Projects Delivered" },
              { k: "100%", v: "Recommend Rate" },
            ].map((s) => (
              <div key={s.v} className="flex items-baseline gap-4">
                <span className="font-display font-bold text-6xl text-brand">{s.k}</span>
                <span className="text-sm uppercase tracking-widest text-white/70">{s.v}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Accreditations />

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-services">
        <div className="container-lf">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div className="max-w-2xl space-y-4">
              <span className="eyebrow">What We Build</span>
              <h2 className="font-display font-bold uppercase text-5xl md:text-7xl leading-[0.95]">
                A full residential<br />construction service.
              </h2>
            </div>
            <Link to="/services" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider hover:text-brand transition-colors" data-testid="home-services-all">
              All services <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Card
                key={s.slug}
                data-testid={`service-card-${s.slug}`}
                className="rounded-none border border-black/10 hover:border-brand overflow-hidden group cursor-pointer transition-all duration-300"
              >
                <Link to="/services" className="block">
                  <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                    <img src={s.image} alt={s.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${s.imageRotate ? "rotate-90 scale-[1.34] group-hover:scale-[1.4]" : ""}`} loading="lazy" />
                    <div className="absolute top-4 left-4 bg-white text-brand-ink px-3 py-1 text-xs font-bold uppercase tracking-widest">
                      0{i + 1}
                    </div>
                  </div>
                  <div className="p-6 md:p-8 space-y-3">
                    <h3 className="font-display font-bold uppercase text-2xl md:text-3xl group-hover:text-brand transition-colors">{s.name}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{s.tagline}</p>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand pt-2">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-32 bg-brand-alt border-y border-black/10" data-testid="home-why">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 space-y-8">
            <span className="eyebrow">Why L F Build</span>
            <h2 className="font-display font-bold uppercase text-5xl md:text-7xl leading-[0.95]">
              Straight-talking<br />builders. <span className="text-brand">Real</span> craft.
            </h2>
            <p className="text-neutral-700 text-lg leading-relaxed">
              We are a family-run firm with 35+ years on the tools across the East of England. Every project is run by a
              named site manager, quoted transparently, and delivered on-programme.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {[
                { icon: ShieldCheck, t: "Fully Insured", d: "£2M public liability + indemnity" },
                { icon: Hammer, t: "In-house Trades", d: "Brickies, roofers, plumbers, sparks" },
                { icon: Clock, t: "On Time", d: "Fixed programmes, weekly updates" },
                { icon: Check, t: "10-Year Guarantee", d: "Workmanship warranty as standard" },
              ].map((f) => (
                <div key={f.t} className="flex gap-4 p-5 bg-white border border-black/10 hover:border-brand transition-colors">
                  <f.icon className="w-6 h-6 text-brand shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <div className="font-display font-bold uppercase text-lg leading-tight">{f.t}</div>
                    <div className="text-sm text-neutral-600 mt-1">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="aspect-[4/5] w-full overflow-hidden border border-black/10">
              <img src="https://customer-assets-v7afamib.emergentagent.net/job_lf-maintenance-site/artifacts/3twcs9v7_FC746C55-5497-42E2-A911-8F064442AD4D.jpeg"
                   alt="Brickwork repair on site" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-8 -left-8 bg-brand text-white p-6 md:p-8 max-w-xs hidden md:block">
              <div className="font-display font-bold text-5xl">35+</div>
              <div className="text-sm uppercase tracking-widest mt-1">Years of combined trade experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-24 md:py-32 bg-white" data-testid="home-areas">
        <div className="container-lf grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5 space-y-6">
            <span className="eyebrow">Where We Work</span>
            <h2 className="font-display font-bold uppercase text-5xl md:text-6xl leading-[0.95]">
              Serving Essex,<br />Suffolk &amp; beyond.
            </h2>
            <p className="text-neutral-600 leading-relaxed">
              Based on the Essex–Suffolk border, we cover the surrounding counties for larger projects. Not sure if we
              reach you? Give us a call — chances are we do.
            </p>
            <Link to="/contact">
              <Button className="rounded-none bg-brand-ink hover:bg-brand text-white h-12 px-6 uppercase tracking-wider text-sm font-semibold">
                Check Availability <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
              {AREAS.map((a) => (
                <div key={a} className="bg-white p-5 flex items-center gap-3 hover:bg-brand hover:text-white transition-colors group">
                  <MapPin className="w-4 h-4 text-brand group-hover:text-white shrink-0" />
                  <span className="font-semibold text-sm uppercase tracking-wider">{a}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-brand-ink text-white" data-testid="home-testimonials">
        <div className="container-lf">
          <div className="max-w-2xl mb-16 space-y-4">
            <span className="eyebrow text-brand">Client Words</span>
            <h2 className="font-display font-bold uppercase text-5xl md:text-7xl leading-[0.95]">
              Trusted by <span className="text-brand">homeowners</span> across the East.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`}
                   className="p-8 border border-white/15 hover:border-brand transition-colors bg-white/[0.02]">
                <div className="flex gap-1 mb-6">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-brand text-brand" />
                  ))}
                </div>
                <p className="text-lg leading-relaxed mb-8 text-white/90">"{t.quote}"</p>
                <div className="pt-6 border-t border-white/15">
                  <div className="font-display font-bold uppercase text-lg">{t.name}</div>
                  <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
